/*
 * Coleção Bracci: botão "Ver mais" (sections/bracci-collection.liquid).
 *
 * O botão é um link de verdade para ?page=N. Sem JavaScript ele navega e mostra o
 * lote seguinte. Com JavaScript, o lote é buscado pela Section Rendering API e
 * anexado aqui. Uma inserção por clique, só quando a pessoa pede: nada de scroll
 * infinito (inserir cards enquanto o cliente rola travava a aba no Safari do iPhone).
 *
 * Com filtro ativo, a mesma linha pode continuar na carga seguinte: os cards dela
 * entram na linha que já está na tela, em vez de repetir o título.
 *
 * Eventos delegados no document: os filtros do Fabric redesenham a seção inteira.
 */
(() => {
  if (window.__bracciCollectionReady) return;
  window.__bracciCollectionReady = true;

  let loading = false;

  function countText(root, count) {
    const template = count === 1 ? root.dataset.countSingular : root.dataset.countPlural;
    return (template || '').replace('[count]', String(count));
  }

  function mergeLines(root, incoming) {
    const target = root.querySelector('[data-bcol-lines]');
    const isDynamic = root.dataset.mode === 'dynamic';
    const fragment = document.createDocumentFragment();
    let firstNew = null;

    incoming.querySelectorAll('[data-bcol-line]').forEach((line) => {
      line.querySelectorAll('link[rel="stylesheet"]').forEach((css) => css.remove());
      const key = line.dataset.bcolLine;
      const existing = target.querySelector(`[data-bcol-line="${CSS.escape(key)}"]`);

      if (existing) {
        const grid = existing.querySelector('[data-bcol-grid]');
        const items = line.querySelectorAll('[data-bcol-grid] > li');
        items.forEach((item) => grid.appendChild(item));
        if (!firstNew && items[0]) firstNew = items[0];
        if (isDynamic) {
          const counter = existing.querySelector('[data-bcol-count]');
          if (counter) counter.textContent = countText(root, grid.children.length);
        }
      } else {
        fragment.appendChild(line);
        if (!firstNew) firstNew = line;
      }
    });

    target.appendChild(fragment);
    return firstNew;
  }

  async function loadMore(link) {
    const root = link.closest('[data-bcol]');
    const sectionId = root?.dataset.sectionId;
    if (!root || !sectionId) return false;

    loading = true;
    link.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`lote ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = doc.querySelector('[data-bcol]');
      if (!incoming || !incoming.querySelector('[data-bcol-line]')) throw new Error('lote vazio');

      const firstNew = mergeLines(root, incoming);

      const nextActions = incoming.querySelector('[data-bcol-actions]');
      const actions = root.querySelector('[data-bcol-actions]');
      if (nextActions && actions) actions.replaceWith(nextActions);
      else actions?.remove();

      // Leva o foco para o começo do lote novo (leitor de tela / teclado) sem pular a tela
      if (firstNew) {
        const focusTarget = firstNew.matches('[data-bcol-line]')
          ? firstNew.querySelector('.bcol-line__title')
          : firstNew.querySelector('a[href]');
        if (focusTarget) {
          if (!focusTarget.matches('a[href]')) focusTarget.setAttribute('tabindex', '-1');
          focusTarget.focus({ preventScroll: true });
        }
      }
      return true;
    } catch (error) {
      console.error('bracci-collection:', error);
      link.removeAttribute('aria-busy');
      return false;
    } finally {
      loading = false;
    }
  }

  /* ---------- Esferas de acabamento ---------- */

  // Alterna a esfera em cima da URL atual (mantém os outros filtros e a ordenação).
  // Marcada → tira os valores dela; desmarcada → soma. Sem nenhuma = todos os produtos.
  function toggledUrl(link) {
    const bar = link.closest('[data-bcf]');
    const param = bar?.dataset.param;
    let values = [];
    try {
      values = JSON.parse(link.dataset.values || '[]');
    } catch (error) {
      values = [];
    }
    if (!param || values.length === 0) return new URL(link.href, window.location.origin);

    const url = new URL(window.location.href);
    const lower = values.map((value) => value.toLowerCase());
    const current = url.searchParams.getAll(param);
    url.searchParams.delete(param);
    if (link.classList.contains('is-active')) {
      current.filter((value) => !lower.includes(value.toLowerCase())).forEach((value) => url.searchParams.append(param, value));
    } else {
      current.forEach((value) => url.searchParams.append(param, value));
      values.forEach((value) => url.searchParams.append(param, value));
    }
    return url;
  }

  // Mesmo caminho dos filtros do Fabric: muda a URL e redesenha a seção pela
  // Section Rendering API. Se algo falhar, o link navega normalmente.
  async function applyFinish(link) {
    const sectionEl = link.closest('.shopify-section');
    const sectionId = sectionEl?.id.replace('shopify-section-', '');
    if (!sectionId) return false;

    try {
      const { sectionRenderer } = await import('@theme/section-renderer');
      link.setAttribute('aria-busy', 'true');
      const url = toggledUrl(link);
      url.searchParams.delete('page');

      // Resposta imediata enquanto a seção é redesenhada
      const bar = link.closest('[data-bcf]');
      link.classList.toggle('is-active');
      link.setAttribute('aria-pressed', String(link.classList.contains('is-active')));
      bar?.classList.toggle('bcf--has-active', !!bar.querySelector('.bcf__item.is-active'));
      history.pushState({ urlParameters: url.searchParams.toString() }, '', url.toString());
      lastSearch = window.location.search;
      await sectionRenderer.renderSection(sectionId);
      initFinishBars();
      return true;
    } catch (error) {
      console.error('bracci-collection:', error);
      return false;
    }
  }

  // Paginação como na faixa da home: cada página mostra só esferas inteiras
  function paging(track) {
    const cells = track.querySelectorAll('.bcf__cell');
    if (cells.length < 2) return { pages: 1, page: 0, size: track.clientWidth };
    const step = cells[1].offsetLeft - cells[0].offsetLeft;
    const perPage = Math.max(1, Math.floor((track.clientWidth + (step - cells[0].offsetWidth)) / step));
    const pages = Math.ceil(cells.length / perPage);
    const size = step * perPage;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    const page = atEnd ? pages - 1 : Math.min(pages - 1, Math.round(track.scrollLeft / size));
    return { pages, page, size };
  }

  function goToPage(track, page) {
    const { pages, size } = paging(track);
    const target = Math.max(0, Math.min(pages - 1, page));
    track.scrollTo({ left: target * size, behavior: 'smooth' });
  }

  function syncArrows(bar) {
    const track = bar?.querySelector('[data-bcf-track]');
    if (!track) return;
    const prev = bar.querySelector('[data-bcf-prev]');
    const next = bar.querySelector('[data-bcf-next]');
    const dots = bar.querySelector('[data-bcf-dots]');
    const overflow = track.scrollWidth > track.clientWidth + 2;
    const { pages, page } = paging(track);

    if (prev && next) {
      prev.hidden = !overflow;
      next.hidden = !overflow;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    }

    if (dots) {
      const count = overflow ? pages : 0;
      if (dots.childElementCount !== count) {
        dots.replaceChildren();
        for (let index = 0; index < count; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'bcf__dot';
          dot.dataset.bcfDot = String(index);
          dot.setAttribute('aria-label', `Página ${index + 1} de ${count}`);
          dot.appendChild(document.createElement('span'));
          dots.appendChild(dot);
        }
      }
      dots.querySelectorAll('.bcf__dot').forEach((dot, index) => {
        dot.classList.toggle('is-active', index === page);
        dot.setAttribute('aria-current', index === page ? 'true' : 'false');
      });
    }
  }

  // Ajusta a largura visível para caber só esferas inteiras (nada cortado nas bordas)
  function fitTrack(track) {
    track.style.removeProperty('width');
    track.style.removeProperty('flex');
    const cells = track.querySelectorAll('.bcf__cell');
    if (cells.length < 2 || track.scrollWidth <= track.clientWidth + 2) return;
    const style = getComputedStyle(track);
    const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const cellWidth = cells[0].offsetWidth;
    const step = cells[1].offsetLeft - cells[0].offsetLeft;
    const gap = step - cellWidth;
    const perPage = Math.max(1, Math.floor((track.clientWidth - padding + gap) / step));
    track.style.flex = '0 0 auto';
    track.style.width = `${perPage * step - gap + padding}px`;
  }

  function initFinishBars() {
    document.querySelectorAll('[data-bcf]').forEach((bar) => {
      const track = bar.querySelector('[data-bcf-track]');
      if (!track) return;
      fitTrack(track);
      // Abre na página do acabamento marcado
      const active = bar.querySelector('.bcf__item.is-active');
      if (active && !bar.dataset.bcfPlaced) {
        bar.dataset.bcfPlaced = 'true';
        const { size } = paging(track);
        const page = Math.floor(active.parentElement.offsetLeft / size);
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = page * size;
        track.style.removeProperty('scroll-behavior');
      }
      syncArrows(bar);
    });
  }

  // scroll não borbulha: ouvindo na captura cobre faixas redesenhadas pelos filtros
  document.addEventListener(
    'scroll',
    (event) => {
      const track = event.target instanceof Element ? event.target.closest('[data-bcf-track]') : null;
      if (track) syncArrows(track.closest('[data-bcf]'));
    },
    true
  );
  document.addEventListener('pointerover', (event) => {
    const bar = event.target instanceof Element ? event.target.closest('[data-bcf]') : null;
    if (bar && !bar.dataset.bcfSynced) {
      bar.dataset.bcfSynced = 'true';
      syncArrows(bar);
    }
  });
  window.addEventListener('resize', initFinishBars);

  // Os filtros do Fabric redesenham a seção inteira (a faixa volta sem pontos)
  let refreshQueued = false;
  new MutationObserver(() => {
    if (refreshQueued || !document.querySelector('[data-bcf]')) return;
    refreshQueued = true;
    requestAnimationFrame(() => {
      refreshQueued = false;
      initFinishBars();
    });
  }).observe(document.body, { childList: true, subtree: true });
  // Voltar/avançar depois de um filtro (esfera ou barra do Fabric): a página não
  // acompanha a URL sozinha, então recarrega. Mudança só de #âncora é ignorada.
  let lastSearch = window.location.search;
  window.addEventListener('popstate', () => {
    if (window.location.search !== lastSearch && document.querySelector('[data-bcol], [data-bcf]')) {
      window.location.reload();
    }
  });
  document.addEventListener('click', () => {
    // Os filtros do Fabric fazem pushState sem avisar; guarda a URL atual a cada interação
    setTimeout(() => (lastSearch = window.location.search), 0);
  });
  document.addEventListener('change', () => {
    setTimeout(() => (lastSearch = window.location.search), 0);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initFinishBars);
  else initFinishBars();

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const arrow = event.target.closest('[data-bcf-prev], [data-bcf-next]');
    if (!arrow) return;
    const track = arrow.closest('[data-bcf]')?.querySelector('[data-bcf-track]');
    if (!track) return;
    const direction = arrow.matches('[data-bcf-prev]') ? -1 : 1;
    goToPage(track, paging(track).page + direction);
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const dot = event.target.closest('[data-bcf-dot]');
    const track = dot?.closest('[data-bcf]')?.querySelector('[data-bcf-track]');
    if (track) goToPage(track, Number(dot.dataset.bcfDot));
  });

  document.addEventListener('click', async (event) => {
    if (!(event.target instanceof Element)) return;
    const finish = event.target.closest('[data-bcol-finish]');
    if (!finish || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    if (finish.getAttribute('aria-busy') === 'true') return;
    const ok = await applyFinish(finish);
    if (!ok) window.location.href = finish.href;
  });

  /* ---------- Ver mais ---------- */

  document.addEventListener('click', async (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[data-bcol-more]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    if (loading) return;
    const ok = await loadMore(link);
    // Falhou: segue o link normalmente (página seguinte completa)
    if (!ok) window.location.href = link.href;
  });
})();
